import { useState, useEffect } from 'react'
import { Footer, Nav } from './App'
// ─── HOOKS ────────────────────────────────────────────────────────────────────

function useWindowWidth(): number {
    const [w, setW] = useState(0)
    useEffect(() => {
        const fn = () => setW(window.innerWidth)
        fn()
        window.addEventListener('resize', fn)
        return () => window.removeEventListener('resize', fn)
    }, [])
    return w
}




function useActiveSection(ids: string[]) {
    const [active, setActive] = useState(ids[0])
    useEffect(() => {
        const handler = () => {
            const scrollPos = window.scrollY + 120
            for (let i = ids.length - 1; i >= 0; i--) {
                const el = document.getElementById(ids[i])
                if (el && el.offsetTop <= scrollPos) {
                    setActive(ids[i])
                    break
                }
            }
        }
        window.addEventListener('scroll', handler, { passive: true })
        return () => window.removeEventListener('scroll', handler)
    }, [ids])
    return active
}

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────

function GlobalStyles() {
    useEffect(() => {
        const s = document.createElement('style')
        s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');
      *,*::before,*::after { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      body { margin: 0; padding: 0; background: #000; color: rgba(255,255,255,0.88); font-family: 'DM Sans', sans-serif; font-weight: 300; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
      body::after { content: ''; position: fixed; inset: 0; pointer-events: none; z-index: 9998; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.05'/%3E%3C/svg%3E"); opacity: 0.25; }
      ::-webkit-scrollbar { width: 5px; }
      ::-webkit-scrollbar-track { background: #000; }
      ::-webkit-scrollbar-thumb { background: rgba(37,89,244,0.35); border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: rgba(37,89,244,0.6); }
      @keyframes riseIn { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes floatA { 0%,100% { transform: translate(0,0); } 50% { transform: translate(20px,-20px); } }
    `
        document.head.appendChild(s)
        return () => { document.head.removeChild(s) }
    }, [])
    return null
}




// ─── SECTION DATA ─────────────────────────────────────────────────────────────

const SECTIONS = [
    { id: 'intro', num: '01', title: 'Introduction & Scope' },
    { id: 'collect', num: '02', title: 'Data We Collect' },
    { id: 'use', num: '03', title: 'How We Use Your Data' },
    { id: 'offline', num: '04', title: 'Offline-First Processing' },
    { id: 'audio', num: '05', title: 'Audio Processing' },
    { id: 'ai', num: '06', title: 'AI & Machine Learning' },
    { id: 'voice', num: '07', title: 'Voice & Audio Data Usage' },
    { id: 'sharing', num: '08', title: 'Data Sharing & Disclosure' },
    { id: 'retention', num: '09', title: 'Data Storage & Retention' },
    { id: 'rights', num: '10', title: 'User Rights' },
    { id: 'ownership', num: '11', title: 'Content Ownership' },
    { id: 'security', num: '12', title: 'Security Measures' },
    { id: 'children', num: '13', title: "Children's Privacy" },
    { id: 'third', num: '14', title: 'Third-Party Services' },
    { id: 'cookies', num: '15', title: 'Cookies & Tracking' },
    { id: 'changes', num: '16', title: 'Changes to This Notice' },
    { id: 'contact', num: '17', title: 'Contact Information' },
]

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────

function Sidebar() {
    const active = useActiveSection(SECTIONS.map(s => s.id))
    const [hovered, setHovered] = useState<string | null>(null)

    const scrollToSection = (id: string) => {
        const el = document.getElementById(id)
        if (el) window.scrollTo({ top: el.offsetTop - 90, behavior: 'smooth' })
    }

    return (
        <aside style={{ position: 'sticky', top: 90, height: 'fit-content', width: 240, flexShrink: 0 }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#2559F4', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'block', width: 16, height: 1, background: '#2559F4' }} />
                Contents
            </div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {SECTIONS.map(s => {
                    const isActive = active === s.id
                    const isHov = hovered === s.id
                    return (
                        <button
                            key={s.id}
                            data-hover
                            onClick={() => scrollToSection(s.id)}
                            onMouseEnter={() => setHovered(s.id)}
                            onMouseLeave={() => setHovered(null)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, background: isActive ? 'rgba(37,89,244,0.08)' : isHov ? 'rgba(255,255,255,0.03)' : 'transparent', border: 'none', borderLeft: `2px solid ${isActive ? '#2559F4' : 'transparent'}`, borderRadius: '0 6px 6px 0', padding: '7px 10px', cursor: 'pointer', textAlign: 'left', transition: 'background .2s, border-color .2s', width: '100%' }}
                        >
                            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, color: isActive ? '#2559F4' : 'rgba(255,255,255,0.25)', minWidth: 22, transition: 'color .2s' }}>{s.num}</span>
                            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: isActive ? '#fff' : isHov ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.38)', transition: 'color .2s', lineHeight: 1.3 }}>{s.title}</span>
                        </button>
                    )
                })}
            </nav>
        </aside>
    )
}

// ─── CONTENT COMPONENTS ───────────────────────────────────────────────────────

function SectionTitle({ num, title }: { num: string; title: string }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#2559F4', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'block', width: 16, height: 1, background: '#2559F4' }} />
                {num}
            </div>
            <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(28px,3.5vw,40px)', fontWeight: 400, letterSpacing: '0.04em', color: '#fff', margin: 0, lineHeight: 1 }}>
                {title}
            </h2>
        </div>
    )
}

function P({ children }: { children: React.ReactNode }) {
    return (
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15, lineHeight: 1.85, color: 'rgba(255,255,255,0.58)', fontWeight: 300, margin: '0 0 18px' }}>
            {children}
        </p>
    )
}

function SubHead({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.01em', margin: '26px 0 10px' }}>
            {children}
        </div>
    )
}

function Highlight({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ background: 'rgba(37,89,244,0.07)', border: '1px solid rgba(37,89,244,0.2)', borderLeft: '3px solid #2559F4', borderRadius: '0 10px 10px 0', padding: '14px 18px', margin: '20px 0', fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.75, color: 'rgba(255,255,255,0.72)', fontWeight: 300 }}>
            {children}
        </div>
    )
}

function Divider() {
    return <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(37,89,244,0.3), rgba(255,255,255,0.06), transparent)', margin: '52px 0' }} />
}

function DataTag({ children }: { children: React.ReactNode }) {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(37,89,244,0.1)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 100, padding: '3px 10px', fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, color: '#2559F4', marginRight: 8, marginBottom: 8 }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#2559F4', display: 'block' }} />
            {children}
        </span>
    )
}

// ─── HERO ─────────────────────────────────────────────────────────────────────

function Hero() {
    const w = useWindowWidth()
    const isMobile = w < 768
    return (
        <section style={{ padding: isMobile ? '100px 20px 60px' : '120px 52px 72px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                <div style={{ position: 'absolute', width: 600, height: 600, top: -200, left: -100, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,89,244,0.14) 0%, transparent 65%)', filter: 'blur(80px)', animation: 'floatA 20s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '44px 44px', maskImage: 'radial-gradient(ellipse 60% 50% at 30% 50%, black 20%, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 30% 50%, black 20%, transparent 70%)' }} />
            </div>

            <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(37,89,244,0.08)', border: '1px solid rgba(37,89,244,0.25)', borderRadius: 100, padding: '5px 14px 5px 10px', marginBottom: 28, animation: 'riseIn .8s cubic-bezier(.16,1,.3,1) .1s both' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2559F4', display: 'block' }} />
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Legal Document</span>
                </div>

                <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(48px,10vw,72px)' : 'clamp(60px,7vw,96px)', fontWeight: 400, lineHeight: 0.92, letterSpacing: '0.04em', color: '#fff', margin: '0 0 24px', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .2s both' }}>
                    Privacy<br />
                    <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Notice.</span>
                </h1>

                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .3s both' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.3)' }}>Last Updated</span>
                        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, fontWeight: 500, color: 'rgba(255,255,255,0.65)', background: 'rgba(255,255,255,0.06)', padding: '3px 10px', borderRadius: 6 }}>April 1, 2026</span>
                    </div>
                    <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.15)', display: 'block' }} />
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.3)' }}>FretPractice · Mobrio Studio</span>
                </div>
            </div>
        </section>
    )
}

// ─── MAIN CONTENT ─────────────────────────────────────────────────────────────

function Content() {
    const w = useWindowWidth()
    const isMobile = w < 768
    const showSidebar = w >= 1100

    const body = (
        <div style={{ flex: 1, minWidth: 0 }}>

            {/* 1 */}
            <section id="intro">
                <SectionTitle num="01" title="Introduction & Scope" />
                <P>This Privacy Notice explains how information about you is collected, used, processed, stored, and protected when you use the FretPractice ecosystem ("Services"), including our applications, desktop tools, AI systems, and mentor platforms.</P>
                <P>FretPractice is an AI-powered music practice and learning ecosystem designed to help users improve their musical skills through intelligent feedback, performance tracking, and AI-generated assistance such as Dhun.</P>
                <P>This Privacy Notice applies to all individuals interacting with our Services, including learners using practice tools, teachers and mentors reviewing sessions, and visitors accessing our website or platform.</P>
                <Highlight>We are committed to protecting your privacy and complying with applicable data protection laws, including the Digital Personal Data Protection Act (DPDPA), 2023 and the Information Technology Act, 2000 (India), wherever applicable.</Highlight>
            </section>

            <Divider />

            {/* 2 */}
            <section id="collect">
                <SectionTitle num="02" title="Data We Collect" />
                <P>We collect information that you provide directly, information generated through your use of the Services, and technical data necessary to operate, secure, and continuously improve the FretPractice platform.</P>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '16px 0 28px' }}>
                    {['Personal Info', 'Practice Data', 'Audio Data', 'AI Interactions', 'Device Data', 'Teacher Platform Data'].map(t => <DataTag key={t}>{t}</DataTag>)}
                </div>

                <SubHead>A. Personal Information</SubHead>
                <P>We collect personal information that you voluntarily provide when creating an account or interacting with the Services. This may include your name, email address, profile details, and login credentials. This information is used to establish and manage your account, authenticate access, enable core features, and communicate with you.</P>

                <SubHead>B. Practice & Performance Data</SubHead>
                <P>As a core part of the FretPractice experience, we collect detailed data related to your musical practice and performance. This includes practice session activity, timing accuracy, pitch accuracy, progress indicators, and overall usage patterns. This data is essential for delivering intelligent feedback, tracking improvement over time, and generating personalized insights.</P>

                <SubHead>C. Audio Data</SubHead>
                <Highlight>We collect and process audio data when you actively use features that involve recording, uploading, or generating sound. Wherever possible, audio analysis such as timing and pitch detection is performed locally on your device to minimize data transfer and enhance privacy.</Highlight>
                <P>We differentiate between transient and persistent data processing. Certain audio inputs may be analyzed in real time without being stored, while recordings you choose to save or upload remain available until you delete them. You retain control over stored audio content at all times.</P>

                <SubHead>D. AI Interaction Data</SubHead>
                <P>We collect data related to your interactions with our AI systems, including Dhun. This includes prompts, inputs, generated outputs, and interaction logs. This information is used to deliver AI-generated music, improve system responsiveness, and enhance the overall quality and relevance of AI-driven features.</P>

                <SubHead>E. Device & Technical Data</SubHead>
                <P>We automatically collect certain technical information when you access or use the Services. This may include your device type, operating system, application version, IP address, browser type, and diagnostic data such as crash logs and performance metrics.</P>

                <SubHead>F. Teacher Platform Data</SubHead>
                <P>For features involving mentors or teachers, we collect and process data related to learning interactions. This includes student submissions, mentor feedback, and session recordings where applicable.</P>

                <Highlight>We are committed to collecting only the data necessary to provide and improve our Services. We do not collect unnecessary or unrelated personal information.</Highlight>
            </section>

            <Divider />

            {/* 3 */}
            <section id="use">
                <SectionTitle num="03" title="How We Use Your Data" />
                <P>We use your information to provide, maintain, and improve the Services in a transparent and purpose-driven manner.</P>
                <P>Your data is used to deliver core functionality such as practice tracking, performance analysis, and AI-generated music assistance. We use performance data to personalize your experience, including recommending exercises, identifying weaknesses, and adapting learning paths.</P>
                <P>We may use certain data to improve our AI models and system accuracy. Where applicable, we clearly disclose whether such data contributes to model training and provide control options.</P>
                <P>We also use your data to communicate with you regarding updates, support requests, and important notifications. Additionally, we process data to maintain platform security, detect misuse, and prevent fraudulent activity.</P>
            </section>

            <Divider />

            {/* 4 */}
            <section id="offline">
                <SectionTitle num="04" title="Offline-First & On-Device Processing" />
                <Highlight>FretPractice is designed with an offline-first approach to maximize user privacy and control.</Highlight>
                <P>Audio analysis, including timing and pitch detection, is primarily performed locally on your device. Desktop features such as stem splitting operate using on-device processing and do not require uploading audio to external servers.</P>
                <P>Minimal data leaves your device unless you explicitly choose to sync, upload, or share content. This approach ensures that sensitive musical data remains under your control.</P>
            </section>

            <Divider />

            {/* 5 */}
            <section id="audio">
                <SectionTitle num="05" title="Audio Processing Transparency" />
                <P>We are committed to transparency in how audio data is handled.</P>
                <P>Audio is recorded only when you initiate recording or use features that require it. We do not access your microphone without explicit user action.</P>
                <P>Audio may be processed in real-time for analysis or stored when you choose to save sessions or share them with mentors. Temporary processing does not result in permanent storage unless explicitly requested.</P>
            </section>

            <Divider />

            {/* 6 */}
            <section id="ai">
                <SectionTitle num="06" title="AI & Machine Learning Transparency" />
                <P>FretPractice uses AI systems, including Dhun, to generate music and provide intelligent feedback.</P>
                <Highlight>We do not use your uploaded audio or personal content to train AI models without your consent.</Highlight>
                <P>We clearly state that user prompts and interactions may be stored to improve system functionality. Our AI models may be trained on a combination of licensed datasets, publicly available data, and anonymized usage patterns. We are committed to ensuring ethical and transparent AI practices.</P>
            </section>

            <Divider />

            {/* 7 */}
            <section id="voice">
                <SectionTitle num="07" title="Voice & Audio Data Usage" />
                <P>If voice or instrument recordings are used for AI features, they are processed solely for the purpose of generating outputs and improving user experience.</P>
                <Highlight>We strictly prohibit impersonation, misuse, or unauthorized replication of voices. Safeguards are in place to prevent misuse of voice-related features.</Highlight>
            </section>

            <Divider />

            {/* 8 */}
            <section id="sharing">
                <SectionTitle num="08" title="Data Sharing & Disclosure" />
                <P>We may share your information with trusted third-party service providers that support our infrastructure, such as cloud storage providers, analytics tools, and AI processing services.</P>
                <Highlight>We do not sell your personal data to third parties.</Highlight>
                <P>Data may also be shared with mentors or collaborators when you explicitly choose to share your content. Additionally, we may disclose information if required by law or to protect the rights and safety of our users and platform.</P>
            </section>

            <Divider />

            {/* 9 */}
            <section id="retention">
                <SectionTitle num="09" title="Data Storage & Retention" />
                <P>We retain your data only for as long as necessary to provide the Services and fulfill legal obligations.</P>
                <P>Practice and performance data is retained until you delete your account. Audio uploads remain stored until you remove them. Certain anonymized or aggregated data may be retained for analytical purposes.</P>
            </section>

            <Divider />

            {/* 10 */}
            <section id="rights">
                <SectionTitle num="10" title="User Rights" />
                <P>You have the right to access, correct, update, or delete your personal data at any time. You may also request a copy of your data or withdraw consent for certain processing activities.</P>
                <Highlight>Where applicable, you can opt out of data usage for AI model improvement. Requests can be made through your account settings or by contacting us directly.</Highlight>
            </section>

            <Divider />

            {/* 11 */}
            <section id="ownership">
                <SectionTitle num="11" title="Content Ownership & Rights" />
                <P>Users retain ownership of the content they upload, including audio recordings and practice data.</P>
                <P>Generated content, including music created using Dhun, is owned by the user unless otherwise specified. FretPractice does not claim ownership of your creative outputs but may retain limited rights necessary to operate and improve the Services.</P>
            </section>

            <Divider />

            {/* 12 */}
            <section id="security">
                <SectionTitle num="12" title="Security Measures" />
                <P>We implement industry-standard security practices to protect your data, including encryption in transit and at rest, secure authentication mechanisms, and strict access controls.</P>
                <P>While we strive to protect your information, no system can guarantee complete security.</P>
            </section>

            <Divider />

            {/* 13 */}
            <section id="children">
                <SectionTitle num="13" title="Children's Privacy" />
                <Highlight>FretPractice is not intended for use by children under the age of 9 without verifiable parental consent. We do not knowingly collect or process personal data from children unless such authorization has been provided.</Highlight>
                <P>If you believe that a child has submitted personal information to us without appropriate consent, please contact us immediately at <span style={{ color: '#2559F4' }}>fretpractice@mobrio.studio</span>, and we will take prompt steps to review and remove such information where required.</P>
            </section>

            <Divider />

            {/* 14 */}
            <section id="third">
                <SectionTitle num="14" title="Third-Party Services" />
                <P>Our Services may integrate with third-party providers such as authentication services, analytics platforms, and future payment gateways. These providers process data in accordance with their own privacy policies.</P>
            </section>

            <Divider />

            {/* 15 */}
            <section id="cookies">
                <SectionTitle num="15" title="Cookies & Tracking" />
                <P>FretPractice uses cookies and related technologies such as pixels, web beacons, and software development kits (SDKs) to understand how users interact with our Services and to enhance overall functionality.</P>

                <SubHead>Essential functionality</SubHead>
                <P>Some cookies are required for the Services to function properly. These support basic operations such as logging into your account, maintaining secure sessions, and accessing core features.</P>

                <SubHead>User preferences and experience</SubHead>
                <P>These technologies allow us to remember your settings and choices, such as language, region, or interface preferences. This ensures that when you return, your experience feels consistent and tailored to you.</P>

                <SubHead>Usage insights and analytics</SubHead>
                <P>We collect information about how users navigate and interact with the Services, including which features are used most frequently. This helps us identify improvements, fix issues, and continuously enhance the product.</P>

                <SubHead>Managing Your Preferences</SubHead>
                <P>By continuing to use our Services, you agree to the use of cookies and similar technologies as described here. You can control or limit their use through your browser or device settings at any time.</P>
                <Highlight>Although some browsers offer "Do Not Track" settings, our Services currently do not respond to these signals due to the absence of a consistent standard across platforms.</Highlight>
            </section>

            <Divider />

            {/* 16 */}
            <section id="changes">
                <SectionTitle num="16" title="Changes to This Privacy Notice" />
                <P>We may update this Privacy Notice from time to time to reflect changes in our Services, legal requirements, or data practices. When we make updates, we will revise the "Last Updated" date at the top of this page.</P>
                <P>For significant changes, we will take additional steps to inform you, such as displaying a notice within the Services or sending you a notification via email. We encourage you to review this Privacy Notice periodically.</P>
                <P>If you do not agree with the updated Privacy Notice, you should discontinue use of the Services.</P>
            </section>

            <Divider />

            {/* 17 */}
            <section id="contact">
                <SectionTitle num="17" title="Contact Information" />
                <P>If you have any questions, concerns, or requests regarding this Privacy Notice or how your data is handled, please contact us at:</P>
                <div style={{ background: '#0a0d17', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '28px 32px', display: 'inline-flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: '#2559F4' }}>
                            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' as const }}>Email us at</div>
                        <a href="mailto:fretpractice@mobrio.studio" data-hover style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 500, color: '#2559F4', textDecoration: 'none', cursor: 'pointer', transition: 'color .2s' }} onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = '#7eb8ff'} onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = '#2559F4'}>
                            fretpractice@mobrio.studio
                        </a>
                    </div>
                </div>

                {/* Delete account link */}
                <div style={{ marginTop: 32, background: 'rgba(192,57,43,0.06)', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 14, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' as const }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: 'rgba(192,57,43,0.85)' }}>
                                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.4-2.29 6.61-5 7.93-2.71-1.32-5-4.53-5-7.93V7.18L12 5z" />
                            </svg>
                        </div>
                        <div>
                            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>Want to delete your account?</div>
                            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.38)', lineHeight: 1.55 }}>
                                You have the right to request erasure of your data under GDPR Article 17. We'll send a confirmation email before anything is deleted.
                            </div>
                        </div>
                    </div>
                    <a
                        href="/delete-account"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: 'rgba(192,57,43,0.9)', background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 8, padding: '9px 18px', textDecoration: 'none', whiteSpace: 'nowrap' as const, transition: 'background 0.2s, border-color 0.2s' }}
                        onMouseEnter={e => { const a = e.currentTarget as HTMLAnchorElement; a.style.background = 'rgba(192,57,43,0.18)'; a.style.borderColor = 'rgba(192,57,43,0.45)' }}
                        onMouseLeave={e => { const a = e.currentTarget as HTMLAnchorElement; a.style.background = 'rgba(192,57,43,0.1)'; a.style.borderColor = 'rgba(192,57,43,0.25)' }}
                    >
                        Request deletion
                        <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor' }}><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" /></svg>
                    </a>
                </div>
            </section>

        </div>
    )

    return (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, paddingBottom: 120 }}>
            <div style={{ display: 'flex', gap: 72, alignItems: 'flex-start' }}>
                {showSidebar && <Sidebar />}
                {body}
            </div>
        </div>
    )
}


// ─── PAGE ─────────────────────────────────────────────────────────────────────

export default function PrivacyPolicy() {
    useEffect(() => {
        const prev = document.title
        document.title = 'Privacy Notice — FretPractice'
        const meta = document.querySelector('meta[name="description"]')
        const prevDesc = meta?.getAttribute('content') || ''
        meta?.setAttribute('content', 'Read the FretPractice Privacy Notice to understand how we collect, use, and protect your data — including audio, practice sessions, and AI interactions.')
        return () => { document.title = prev; meta?.setAttribute('content', prevDesc) }
    }, [])
    return (
        <>
            <GlobalStyles />
            <Nav />
            <Hero />
            <Content />
            <Footer />
        </>
    )
}